/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  getAvailableOrganicRankManagementReq,
  getOrganicRankManagementReq,
  patchOrganicRankManagementReq,
} from '../../api/organicManagement'
//  MOCK DATA
// import {
//   AVAILABLE_ORGANIC_RANK_MOCK_CONFIG,
//   MOCK_API_DATA
// } from '../../components/ProductManageModal/Rank/fakeApi'

const chartKeys = {
  organicRanks_keywords: 'organicRanks_keywords',
  integratedRank_keywords: 'integratedRank_keywords',
  proportionAdvertising_keywords: 'proportionAdvertising_keywords',
  otherRanks_keywords_advertising: 'otherRanks_keywords_advertising',
  organicImpressions_keywords: 'organicImpressions_keywords',
  advertisingImpressions_keywords: 'advertisingImpressions_keywords',
  advertisingImpressionShare_keywords: 'advertisingImpressionShare_keywords',
  totalImpressions_keywords: 'totalImpressions_keywords',
}

const configKeys = {
  currentOrganicRank: 'organicRanks_keywords',
  currentIntegratedRank: 'integratedRank_keywords',
  currentProportionAdvertising: 'proportionAdvertising_keywords',
  currentAdvertisingRank: 'otherRanks_keywords_advertising',
  currentOrganicImpressions: 'organicImpressions_keywords',
  currentAdvertisingImpressions: 'advertisingImpressions_keywords',
  // TODO: not correct relatives
  totalImpressions_keywords: 'hidden',
}

export const EMPTY_KEYWORD = {
  currentBidFactor: 0,
  currentBidFactorInit: 0,
  currentFixedBid: {
    fixedBid_exact: null,
    fixedBid_phrase: null,
    fixedBid_exact_init: null,
    fixedBid_phrase_init: null,
  },
  currentRank: 0,
  currentSearchVolume: {
    value: 0,
    asOf: 'NA',
  },
  currentImpressions: {
    value: null,
    asOf: 'NA',
  },
  currentImpressionShare: {
    value: null,
    asOf: 'NA',
  },
  currentClicks: {
    value: null,
    asOf: 'NA',
  },
  currentConversions: {
    value: null,
    asOf: 'NA',
  },
  currentACoS: {
    value: null,
    asOf: 'NA',
  },
  type: 'keyword',
  searchPhrase: '',
  searchPhraseInit: '',
  currentOrganicRank: {
    value: null,
    asOf: 'NA',
  },
  currentIntegratedRank: {
    value: null,
    asOf: 'NA',
  },
  currentProportionAdvertising: {
    value: null,
    asOf: 'NA',
  },
  currentAdvertisingRank: {
    value: null,
    asOf: 'NA',
  },
  currentOrganicImpressions: {
    value: null,
    asOf: 'NA',
  },
  currentAdvertisingImpressions: {
    value: null,
    asOf: 'NA',
  },
  totalImpressions_keywords: {
    value: null,
    asOf: 'NA',
  },
}

const mapChartData = (data) => {
  const chartData = {}

  Object.keys(data).forEach((key) => {
    if (chartKeys[key]) {
      chartData[key] = data[key].map((item, index) => {
        const labels = {
          [-1]: `Week -2`,
          [-2]: `Week -3`,
          [-3]: `Week -4`,
          [-4]: `Week -5`,
        }
        return {
          week: !item.relativeWeek ? 'Last 7 days' : labels[item.relativeWeek],
          value: item.value?.toFixed(2),
          index,
        }
      })
    }
  })

  return chartData
}

const mapEntityConfiguration = (data = []) =>
  data.map((item, index) => {
    const mappedRank = {}

    Object.keys(item).forEach((key) => {
      if (configKeys[key]) {
        mappedRank[configKeys[key]] = item[key]
      }
    })

    return {
      ...item,
      ...mappedRank,
      searchPhraseInit: item.searchPhrase,
      id: `${item.searchPhrase}-${index}`,
      currentBidFactorInit: item.currentBidFactor,
      currentFixedBid: {
        ...item.currentFixedBid,
        fixedBid_exact_init: item.currentFixedBid.fixedBid_exact,
        fixedBid_phrase_init: item.currentFixedBid.fixedBid_phrase,
      },
    }
  })

const mapAvailableToAddKeywords = (data = []) =>
  data
    .filter((item) => item.type === 'keyword')
    .map((item, index) => ({
      ...EMPTY_KEYWORD,
      id: `${item.searchPhrase}-${index}`,
      searchPhrase: item.searchPhrase,
    }))

const options = [
  {
    label: 'Organic rank',
    value: 'organicRanks_keywords',
    mainLabel: '5-week avg. organic rank development',
  },
  {
    label: 'Impressions (advertising)',
    value: 'advertisingImpressions_keywords',
    mainLabel: '5-week development of cum. advertising impressions',
  },
  {
    label: 'Impressions share (advertising)',
    value: 'advertisingImpressionShare_keywords',
    mainLabel: '5-week development of agg. advertising impressions share',
  },
]

const initialState = {
  entityConfiguration: [],
  chartData: [],
  availableToAddKeywords: [],
  keysToDelete: [],
  maxEntities: 0,
  selectedOrganicRankModalOption: options[0].value,
  options,
  isDataFetching: false,
  isAvailableToAddKeywordsLoading: false,
  isItemsUpdating: false,
  additionalInfo: {
    productID: null,
    accountID: null,
    channelID: null,
    isBol: true,
  },
}

export const getOrganicRankManagementAction = createAsyncThunk(
  'organicRankModal/getOrganicRankManagementAction',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getOrganicRankManagementReq(data)
      return {
        data: {
          ...response?.data,
          organicRanksKeywords: response?.data?.organicRanks_keywords || [],
          entityConfiguration: response?.data?.entityConfiguration || [],
        },
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getOrganicRankManagementKeywordAction = createAsyncThunk(
  'organicRankModal/getOrganicRankManagementKeywordAction',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getOrganicRankManagementReq(data)
      return {
        data: {
          ...response?.data,
          entityConfiguration: response?.data?.entityConfiguration || [],
        },
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getOrganicRankManagementAvailableToAddKeywordsAction = createAsyncThunk(
  'organicRankModal/getOrganicRankManagementAvailableToAddKeywordsAction',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getAvailableOrganicRankManagementReq(data)
      return {
        data: response?.data || [],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const patchOrganicRankManagementKeywordsAction = createAsyncThunk(
  'organicRankModal/patchOrganicRankManagementKeywordsAction',
  async (data, { rejectWithValue }) => {
    try {
      const response = await patchOrganicRankManagementReq(data)
      return {
        data: {
          organicRanksKeywords: response?.data?.organicRanksKeywords || [],
        },
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deleteOrganicRankManagementKeywordsAction = createAsyncThunk(
  'organicRankModal/deleteOrganicRankManagementKeywordsAction',
  async (data, { rejectWithValue }) => {
    try {
      await patchOrganicRankManagementReq(data)
      return data.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const organicRankModalSlice = createSlice({
  name: 'organicRankModal',
  initialState,
  reducers: {
    setAdditionalInfo: (state, action) => {
      state.additionalInfo = {
        ...action.payload,
      }
    },
    updateEntityConfigurationItem: (state, action) => {
      state.entityConfiguration = state.entityConfiguration.map((item) => {
        const { id, data } = action.payload
        if (item.id === id) {
          return {
            ...item,
            ...data,
          }
        }
        return item
      })
    },
    addNewEntityConfigurationItemTemplate: (state, action) => {
      state.entityConfiguration = [action.payload, ...state.entityConfiguration]
    },
    replaceTemplateItemByTemporaryEntityConfigurationItem: (state, action) => {
      state.entityConfiguration = state.entityConfiguration.map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            ...action.payload.item,
            type: 'keyword_temp',
          }
        }
        return item
      })
    },
    removeNewEntityConfigurationItemTemplate: (state, action) => {
      state.entityConfiguration = state.entityConfiguration.filter((item) => item.id !== action.payload)
    },
    removeEntityConfigurationItem: (state, action) => {
      state.entityConfiguration = state.entityConfiguration.filter((item) => item.id !== action.payload)
    },
    setKeysToDelete: (state, action) => {
      state.keysToDelete = [...state.keysToDelete, action.payload]
    },
    setSelectedOrganicRankModalOption: (state, action) => {
      state.selectedOrganicRankModalOption = action.payload
    },
    dispose: (state) => {
      state.entityConfiguration = []
      state.chartData = []
      state.availableToAddKeywords = []
      state.keysToDelete = []
      state.maxEntities = 0
      state.selectedOrganicRankModalOption = options[0].value
      state.options = options
      state.isDataFetching = false
      state.isAvailableToAddKeywordsLoading = false
      state.isItemsUpdating = false
      state.additionalInfo = {
        productID: null,
        accountID: null,
        channelID: null,
        isBol: true,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // get data
      .addCase(getOrganicRankManagementAction.pending, (state) => {
        state.isDataFetching = true
      })
      .addCase(getOrganicRankManagementAction.fulfilled, (state, actions) => {
        const data = actions.payload.data?.entityConfiguration || []
        const filtered = data.filter((item) => item.type === 'keyword')
        state.entityConfiguration = mapEntityConfiguration(filtered)
        state.chartData = mapChartData(actions.payload.data)
        state.maxEntities = actions.payload.data.maxEntities || 0
        state.isDataFetching = false
        state.maxEntities = 10
        // MOCK DATA
        // state.entityConfiguration = mapEntityConfiguration(MOCK_API_DATA.entityConfiguration)
        // state.chartData = mapChartData(MOCK_API_DATA)
      })
      .addCase(getOrganicRankManagementAction.rejected, (state) => {
        // MOCK DATA
        // state.entityConfiguration = mapEntityConfiguration(ORGANIC_RANK_MOCK_CONFIG)
        // state.chartData = mapChartData(ORGANIC_RANK_CHART_MOCK_DATA)
        // state.maxEntities = 10
        state.isDataFetching = false
      })
      .addCase(getOrganicRankManagementKeywordAction.pending, (state) => {
        state.isItemsUpdating = true
      })
      .addCase(getOrganicRankManagementKeywordAction.fulfilled, (state, actions) => {
        const data = actions.payload.data?.entityConfiguration || []
        const filtered = data.filter((item) => item.type === 'keyword')
        state.entityConfiguration = mapEntityConfiguration(filtered)
        state.maxEntities = actions.payload.data.maxEntities || 0
        state.isItemsUpdating = false
      })
      .addCase(getOrganicRankManagementKeywordAction.rejected, (state) => {
        state.isItemsUpdating = false
      })
      // get available keywords to add
      .addCase(getOrganicRankManagementAvailableToAddKeywordsAction.pending, (state) => {
        state.isAvailableToAddKeywordsLoading = true
      })
      .addCase(getOrganicRankManagementAvailableToAddKeywordsAction.fulfilled, (state, actions) => {
        state.availableToAddKeywords = mapAvailableToAddKeywords(actions.payload.data)
        state.isAvailableToAddKeywordsLoading = false
        // MOCK DATA
        // state.availableToAddKeywords = mapAvailableToAddKeywords(AVAILABLE_ORGANIC_RANK_MOCK_CONFIG)
      })
      .addCase(getOrganicRankManagementAvailableToAddKeywordsAction.rejected, (state) => {
        // MOCK DATA
        // state.availableToAddKeywords = mapEntityConfiguration(AVAILABLE_ORGANIC_RANK_MOCK_CONFIG)
        state.isAvailableToAddKeywordsLoading = false
      })
      // patch data
      .addCase(patchOrganicRankManagementKeywordsAction.pending, (state) => {
        state.isItemsUpdating = true
      })
      .addCase(patchOrganicRankManagementKeywordsAction.fulfilled, (state, actions) => {
        const data = actions.payload.data?.entityConfiguration || []
        const filtered = data.filter((item) => item.type === 'keyword')
        state.entityConfiguration = mapEntityConfiguration(filtered)
        state.isItemsUpdating = false
      })
      .addCase(patchOrganicRankManagementKeywordsAction.rejected, (state) => {
        state.isItemsUpdating = false
      })
      // delete data
      .addCase(deleteOrganicRankManagementKeywordsAction.pending, (state) => {
        state.isItemsUpdating = true
      })
      .addCase(deleteOrganicRankManagementKeywordsAction.fulfilled, (state, actions) => {
        state.entityConfiguration = state.entityConfiguration.filter(
          (item) => !actions.payload.find((delItem) => delItem.searchPhrase === item.searchPhrase)
        )
        state.isItemsUpdating = false
      })
      .addCase(deleteOrganicRankManagementKeywordsAction.rejected, (state) => {
        state.isItemsUpdating = false
      })
  },
})

// state
export const selectEntityConfiguration = (state) => state.organicRankModal.entityConfiguration
export const selectOrganicRanksChartData = (state) => state.organicRankModal.chartData
export const selectOrganicRanksMaxKeywordNumberData = (state) => state.organicRankModal.maxEntities
export const selectOrganicRanksAvailableToAddKeywordsData = (state) => state.organicRankModal.availableToAddKeywords
export const selectOrganicRanksAdditionalInfo = (state) => state.organicRankModal.additionalInfo
export const selectOrganicRanksOptions = (state) => state.organicRankModal.options
export const selectOrganicRanksSelectedOption = (state) => state.organicRankModal.selectedOrganicRankModalOption
export const selectOrganicRanksKeysToDelete = (state) => state.organicRankModal.keysToDelete
// Loaders
export const selectIsOrganicManagementDataLoading = (state) => state.organicRankModal.isDataFetching
export const selectIsOrganicManagementAvailableToAddKeywordsLoading = (state) =>
  state.organicRankModal.isAvailableToAddKeywordsLoading
export const selectIsItemsUpdating = (state) => state.organicRankModal.isItemsUpdating
// actions
export const {
  updateEntityConfigurationItem,
  addNewEntityConfigurationItemTemplate,
  replaceTemplateItemByTemporaryEntityConfigurationItem,
  removeNewEntityConfigurationItemTemplate,
  removeEntityConfigurationItem,
  setAdditionalInfo,
  setSelectedOrganicRankModalOption,
  setKeysToDelete,
  dispose,
} = organicRankModalSlice.actions

export default organicRankModalSlice.reducer
